import React, { useMemo, useState, memo } from 'react';
import { PageLayout } from '../../shared/PageLayout';
import { ConnectionBadge } from '../shared/ConnectionBadge';
import { StatCard } from '../../shared/StatCard';
import { SeedStatus } from '../system/diagnostics/SeedStatus';
import { computeLiveSystemStats } from '../../../governance';
import { QUESTIONS_BY_FAMILY, TAXONOMY } from '../../../content';
import { useAppStore } from '../../../app/hooks/useAppStore';
import { AdminTabs } from '../shared/AdminTabs';
import { Database, GitBranch, Cpu, Clock, HardDrive, Activity, Hash, Tag } from 'lucide-react';
import { dataLayer } from '../../../data/dataLayer';
const tabs = [{
  id: 'seeds',
  label: 'Seeds',
  icon: <Database className="w-4 h-4" data-id="element-4076" />
}, {
  id: 'system',
  label: 'Systeem',
  icon: <Cpu className="w-4 h-4" data-id="element-4077" />
}] as const;
type TabId = (typeof tabs)[number]['id'];
function ResourceBar({
  label,
  value,
  color
}: {
  label: string;
  value: number | string;
  color: string;
}) {
  const isNumber = typeof value === 'number';
  const displayValue = isNumber ? `${value}%` : value;
  const percentValue = isNumber ? value : 0;
  return <div data-id="element-4078">
      <div className="flex justify-between text-xs mb-1.5" data-id="element-4079">
        <span className="text-gray-700 font-medium" data-id="element-4080">{label}</span>
        <span className={`font-semibold ${isNumber && value > 80 ? 'text-red-500' : isNumber && value > 60 ? 'text-yellow-600' : 'text-gray-600'}`} data-id="element-4081">
          {displayValue}
        </span>
      </div>
      <div className="w-full bg-gray-100 rounded-full h-2" data-id="element-4082">
        <div className={`${color} h-2 rounded-full transition-all`} style={{
        width: `${percentValue}%`
      }} data-id="element-4083" />
      </div>
    </div>;
}
function SystemPanel() {
  const {
    accounts
  } = useAppStore();
  const diagnostics = useMemo(() => {
    const storageStats = dataLayer.getStorageStats();
    const storageMb = storageStats.usedMb;
    const activeAccounts = accounts?.accountsById ? Object.values(accounts.accountsById).filter(a => Date.now() - (a.lastActive || 0) < 60 * 60 * 1000).length : 0;
    let responseTime = 45;
    try {
      if (window.performance && window.performance.timing) {
        responseTime = window.performance.timing.responseEnd - window.performance.timing.requestStart;
      }
    } catch {
      // Performance timing API may be unavailable
    }
    return {
      storageMb,
      activeAccounts,
      responseTime: Math.max(10, responseTime)
    };
  }, [accounts]);
  const resourceMetrics = useMemo(() => {
    let memoryUsage = 0;
    try {
      const perfWithMemory = window.performance as Performance & {
        memory?: {
          usedJSHeapSize: number;
          jsHeapSizeLimit: number;
        };
      };
      if (perfWithMemory.memory) {
        memoryUsage = Math.round(perfWithMemory.memory.usedJSHeapSize / perfWithMemory.memory.jsHeapSizeLimit * 100);
      } else {
        memoryUsage = Math.min(100, Math.round(parseFloat(diagnostics.storageMb) / 5 * 100));
      }
    } catch {
      // Memory API may be unavailable in some browsers
    }
    const storageUsage = Math.min(100, Math.round(parseFloat(diagnostics.storageMb) / 5 * 100));
    return {
      memory: memoryUsage,
      storage: storageUsage
    };
  }, [diagnostics.storageMb]);
  const environmentRows = [{
    label: 'Build mode',
    value: typeof import.meta !== 'undefined' && import.meta.env?.MODE ? import.meta.env.MODE : 'development'
  }, {
    label: 'Lokale opslag',
    value: `${diagnostics.storageMb} MB`
  }, {
    label: 'Actieve accounts',
    value: diagnostics.activeAccounts.toString()
  }];
  return <div className="space-y-6" data-id="element-4084">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4" data-id="element-4085">
        <StatCard label="Laadtijd" value={`${diagnostics.responseTime}ms`} subtitle="gemiddeld" icon={<Clock className="w-5 h-5" data-id="element-4087" />} color="amber" data-id="element-4086" />
        <StatCard label="Opslag" value={`${diagnostics.storageMb} MB`} subtitle="Lokale opslag" icon={<HardDrive className="w-5 h-5" data-id="element-4089" />} color="blue" data-id="element-4088" />
        <StatCard label="Actieve Sessies" value={diagnostics.activeAccounts} subtitle="Laatste uur" icon={<Activity className="w-5 h-5" data-id="element-4091" />} color="purple" data-id="element-4090" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6" data-id="element-4092">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-4093">
          <div className="px-4 sm:px-5 py-4 border-b border-gray-100 bg-gray-50" data-id="element-4094">
            <h3 className="text-sm font-semibold text-gray-900" data-id="element-4095">
              Systeembronnen
            </h3>
          </div>
          <div className="p-4 sm:p-5 space-y-4" data-id="element-4096">
            <ResourceBar label="Geheugen" value={resourceMetrics.memory} color="bg-purple-500" data-id="element-4097" />
            <ResourceBar label="Opslag" value={resourceMetrics.storage} color="bg-green-500" data-id="element-4098" />
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-4099">
          <div className="px-4 sm:px-5 py-4 border-b border-gray-100 bg-gray-50" data-id="element-4100">
            <h3 className="text-sm font-semibold text-gray-900" data-id="element-4101">Omgeving</h3>
          </div>
          <div className="divide-y divide-gray-50" data-id="element-4102">
            {environmentRows.map((row, i) => <div key={i} className="px-4 sm:px-5 py-3 flex justify-between items-center text-sm" data-id="element-4103">
                <span className="text-gray-600" data-id="element-4104">{row.label}</span>
                <span className="font-medium text-gray-900" data-id="element-4105">{row.value}</span>
              </div>)}
          </div>
        </div>
      </div>
    </div>;
}
export function DiagnosticsPage() {
  const [activeTab, setActiveTab] = useState<TabId>('seeds');
  const stats = useMemo(() => computeLiveSystemStats(), []);
  return <PageLayout title="Systeem Diagnostiek" subtitle="Technische status en systeembronnen" actions={<ConnectionBadge status="live" data-id="element-4107" />} breadcrumbs={[{
    label: 'Systeem'
  }, {
    label: 'Diagnostiek'
  }]} data-id="element-4106">
      {/* Overview Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-6" data-id="element-4108">
        <StatCard label="Totaal Vragen" value={stats.totalQuestions.toLocaleString('nl-NL')} icon={<Hash className="w-5 h-5" data-id="element-4110" />} color="blue" data-id="element-4109" />
        <StatCard label="Families" value={QUESTIONS_BY_FAMILY.size} icon={<Tag className="w-5 h-5" data-id="element-4112" />} color="purple" data-id="element-4111" />
        <StatCard label="Taxonomie" value={TAXONOMY.length} icon={<GitBranch className="w-5 h-5" data-id="element-4114" />} color="emerald" data-id="element-4113" />
        <StatCard label="Geheugen" value={`${Math.round(stats.memoryUsageMB)} MB`} icon={<Cpu className="w-5 h-5" data-id="element-4116" />} color="amber" data-id="element-4115" />
      </div>

      <AdminTabs tabs={tabs} activeTab={activeTab} onTabChange={id => setActiveTab(id as TabId)} data-id="element-4117" />

      <div className="mt-4" data-id="element-4118">
        {activeTab === 'seeds' && <SeedStatus data-id="element-4119" />}
        {activeTab === 'system' && <SystemPanel data-id="element-4120" />}
      </div>
    </PageLayout>;
}